import React, { useState,useEffect } from 'react';
import Select, { components } from 'react-select';
import { useParams } from "react-router-dom";
import { Tooltip as TooltipLite } from 'react-tooltip';
import FullPageLoader from '../../src/common/FullPageLoader';
import ConfirmPopUp from '../../src/common/ConfirmPopUp';
import { useDispatch } from 'react-redux';
import { getMasterConfig,addDocument,DeleteDocProof } from '../../src/store/slices/common';
import { ReceivedDate } from './DatePicker';
import * as HELPER from "../../src/config/helper";
import { offsetAgentCommission, updateLeadPaymentConfig } from '../../src/store/slices/insurance';
import FollowUpPopup from '../lead/common/FollowUpPopup';
import { LEAD_LOST_SUB_STATUS_ID, RAGEX_NUMBER_UPTO_2_DECIMAL_PLACES } from '../config/constant';
import OffsetAgentCommissionPopup from './OffsetAgentCommissionPopup';
import OffsetAgentCommissionIntroPopup from './OffsetAgentCommissionIntroPopup';
import AdditionalPaymentSection, { AdditionalPayment } from './AdditionalPaymentSection';

interface PaymentProof {
  id?: number;
  doc_path?: string;
  status?: number;
  original_url?: string;
  file?: File;
}

const { ValueContainer, Placeholder } = components;

const CustomValueContainer = (props: any) => {
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {React.Children.map(props.children, (child: any) =>
        child && child.type !== Placeholder ? child : null
      )}
    </ValueContainer>
  );
};

const getSelectedOption = (options: any[], value: any) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    return options.find((option: any) => String(option?.value) === String(value)) || null;
};

const MAX_ADDITIONAL_PAYMENTS = 10;
const ALLOWED_PAYMENT_PROOF_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

const createAdditionalPayment = (): AdditionalPayment => ({
    id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    payment_made_from: null,
    payment_made_to: null,
    payment_amount_received: '',
    payment_received_date: null,
});

const mapSavedAdditionalPayments = (payments: any[] = []): AdditionalPayment[] => {
    return payments.map((payment, index) => {
        const proofPath = payment.proof_of_payment || payment.doc_path || payment.original_url || '';
        return {
            id: `saved_${payment.id || index}`,
            payment_made_from: payment.payment_from ?? payment.payment_made_from ?? null,
            payment_made_to: payment.payment_to ?? payment.payment_made_to ?? null,
            payment_amount_received: String(payment.received_amount ?? payment.payment_amount_received ?? ''),
            payment_received_date: payment.received_amount_date ?? payment.payment_received_date ?? null,
            excess_amount_with_oto: payment.excess_amount_with_oto,
            proof_of_payment: proofPath
                ? {
                    doc_path: proofPath,
                    original_url: payment.original_url || proofPath,
                }
                : undefined,
            is_saved: true,
        };
    });
};

const toAmount = (value: any) => {
    const amount = Number(HELPER.normalizeIdDecimalAmount(value));
    return Number.isFinite(amount) ? amount : 0;
};

const isTruthyValue = (value: any) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return ['1', 'true', 'yes'].includes(value.trim().toLowerCase());
    return false;
};

const getOffsetAgentCommissionTooltip = (offsetInfo: any) => {
    const offsetAmount = toAmount(offsetInfo?.offset_agent_commission_amount);

    if (!isTruthyValue(offsetInfo?.is_agent_commission_offset) || offsetAmount <= 0) {
        return '';
    }

    return `${HELPER.addIdDecimalCurrencyFormatting(offsetAmount)} Received from Offset Agent Commission`;
};

const roundAmount = (value: number) => Number(value.toFixed(2));

const getOutstandingAmount = (value: any) => Math.abs(Math.min(toAmount(value), 0));

const getAdditionalPaymentTotal = (payments: AdditionalPayment[]) => {
    return roundAmount(payments.reduce((sum, payment) => sum + toAmount(payment.payment_amount_received), 0));
};

const getAdditionalPaymentBaseExcessAmount = (paymentFormState: any, payments: AdditionalPayment[]) => {
    const additionalPaymentTotal = getAdditionalPaymentTotal(payments);
    const baseReceivedAmount = roundAmount(toAmount(paymentFormState.total_premium_amount_received) - additionalPaymentTotal);

    return roundAmount(
        baseReceivedAmount - toAmount(paymentFormState.insurance_premium_amount)
    );
};

const getAdditionalPaymentExcessAmount = (paymentFormState: any, payments: AdditionalPayment[], index: number) => {
    const baseExcessAmount = getAdditionalPaymentBaseExcessAmount(paymentFormState, payments);
    const receivedAmountUntilCurrentPayment = getAdditionalPaymentTotal(payments.slice(0, index + 1));
    return roundAmount(baseExcessAmount + receivedAmountUntilCurrentPayment);
};

const getSelectedPaymentMethod = (options: any[], value: any) => {
    const selected = getSelectedOption(options, value);
    const haystack = `${selected?.label || ''} ${selected?.name || ''} ${selected?.slug || ''}`.toLowerCase();
    return {
        selected,
        isXendit: haystack.includes('xendit'),
    };
};

const isFutureDate = (dateValue: string | null) => {
    if (!dateValue) return false;
    const selectedDate = new Date(dateValue);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return selectedDate > today;
};

const getUploadResponseFiles = (uploadDocument: any) => {
    if (Array.isArray(uploadDocument?.data?.data)) return uploadDocument.data.data;
    if (Array.isArray(uploadDocument?.data)) return uploadDocument.data;
    if (Array.isArray(uploadDocument)) return uploadDocument;
    return [];
};

const getUploadResponseStatus = (uploadDocument: any) => uploadDocument?.data?.status ?? uploadDocument?.status;

const isEmptyValue = (value: any) => value === null || value === undefined || value === '';

const toPayloadNumber = (value: any) => Number(value);

const normalizePaymentPayloadAmounts = (paymentForm: any) => {
    const normalizedPaymentForm = { ...paymentForm };
    ['insurance_premium_amount', 'total_premium_amount_received', 'excess_amount_with_oto'].forEach((field) => {
        if (normalizedPaymentForm[field] !== null && normalizedPaymentForm[field] !== undefined && normalizedPaymentForm[field] !== '') {
            normalizedPaymentForm[field] = roundAmount(toAmount(normalizedPaymentForm[field]));
        }
    });
    return normalizedPaymentForm;
};

const LeadDetailFormID = (props:any) => {
    const { IsFromXendit,IsPaymentFromAgent,paymentConfig,reloadEmiDetails,isPushedToRemittance,leadSubStatusId } = props
    const isFormDisabled = isPushedToRemittance;
    // WITHOUT XENDIT
    const isWithoutXenditAgent = !IsFromXendit && IsPaymentFromAgent === true;
    const isWithoutXenditCustomer = !IsFromXendit && IsPaymentFromAgent === false;
    // WITH XENDIT (Agent or Customer)
    const isWithXendit = IsFromXendit;
    const [proofOfPayment,setProofOfPayment] = useState<PaymentProof[]>([{} as PaymentProof,{} as PaymentProof]);

    const dispatch: any = useDispatch();
    const { id } = useParams<{ id: string }>()

    const [form, setForm] = useState<any>({
        payment_made_from: null,
        payment_made_to: null,
        payment_status:null,
        basic_premium: "",
        ads_premium: "",
        admin_fee: "",
        net_premium: "",
        gross_written_premium: "",
        oto_commission_rate: "",
        oto_gross_profit: "",
        customer_discount_percent: "",
        customer_discount_value: "",
        agent_commission_percent: "",
        agent_commission_value: "",
        insurance_premium_amount: "",
        total_premium_amount_received: "",
        excess_amount_with_oto: "",
        payment_method: null,
        premium_paid_date: null,
        payment_refrence_number: "",
        transaction_fee_paid_by_customer: "",
        xendit_fee:"",
        other_income:"",
        amount_received_from_partner_after_remittance:""
    });

    const [loading, setLoading] = useState<boolean>(false);

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [paymentSubmitMode, setPaymentSubmitMode] = useState<'main' | 'additional'>('main');
    const [deleteConfirmation,setDeleteConfirmation] = useState(false);

    const [errors, setErrors] = useState<any>({});

    const [paymentMadeFrom,setPaymentMadeFrom] = useState<any[]>([]);
    const [paidTo, setPaidTo] = useState<any[]>([]);
    const [paymentStatus, setPaymentStatus] = useState<any[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<any[]>([]);
    const [followUpPopup, setFollowUpPopup] = useState(false);
    const [isLostStatusVisible] = useState<boolean>(true);
    const [recordToBeDeleted,setRecordToBeDeleted] = useState<any>({});
    const [showOffsetAgentCommissionIntroPopup, setShowOffsetAgentCommissionIntroPopup] = useState(false);
    const [showOffsetAgentCommissionPopup, setShowOffsetAgentCommissionPopup] = useState(false);
    const [savedAdditionalPayments, setSavedAdditionalPayments] = useState<AdditionalPayment[]>([]);
    const [additionalPayments, setAdditionalPayments] = useState<AdditionalPayment[]>([]);
    const [additionalPaymentErrors, setAdditionalPaymentErrors] = useState<Record<string, string>>({});
    const [offsetAgentCommissionInfo, setOffsetAgentCommissionInfo] = useState<any>({});

    const selectedPaymentMethod = getSelectedPaymentMethod(paymentMethod, form.payment_method);
    const isNonXenditPaymentMode = !isWithXendit && !selectedPaymentMethod.isXendit;
    const shouldShowOutstandingRecovery = isNonXenditPaymentMode && toAmount(form.excess_amount_with_oto) < 0;
    const isOffsetAgentCommissionUsed = isTruthyValue(offsetAgentCommissionInfo?.is_agent_commission_offset);
    const offsetAgentCommissionTooltip = getOffsetAgentCommissionTooltip(offsetAgentCommissionInfo);
    const shouldShowOffsetAgentCommissionInfo = Boolean(offsetAgentCommissionTooltip);
    const totalAdditionalPaymentCount = savedAdditionalPayments.length + additionalPayments.length;
    const isAdditionalPaymentLimitReached = totalAdditionalPaymentCount >= MAX_ADDITIONAL_PAYMENTS;
    const latestSavedAdditionalPayment = savedAdditionalPayments[savedAdditionalPayments.length - 1];
    const isOutstandingPremiumFullyRecovered = (
        isNonXenditPaymentMode &&
        !!latestSavedAdditionalPayment &&
        roundAmount(toAmount(latestSavedAdditionalPayment.excess_amount_with_oto)) === 0
    );

    const buildAdditionalPaymentsPayload = (
        paymentsToBuild: AdditionalPayment[] = additionalPayments,
        additionalProofFileNames: Record<string, string> = {},
        paymentFormState: any = form
    ) => {
        return paymentsToBuild.map((payment, index) => ({
            payment_from: toPayloadNumber(payment.payment_made_from),
            payment_to: toPayloadNumber(payment.payment_made_to),
            received_amount: roundAmount(toAmount(payment.payment_amount_received)),
            received_amount_date: payment.payment_received_date,
            excess_amount_with_oto: getAdditionalPaymentExcessAmount(paymentFormState, paymentsToBuild, index),
            proof_of_payment: additionalProofFileNames[payment.id] || '',
        }));
    };

    const submitPaymentConfig = async (
        paymentFormState: any,
        options: {
            additionalPaymentsToSave?: AdditionalPayment[];
        } = {}
    ) => {
        setLoading(true);
        const additionalPaymentsToSave = isNonXenditPaymentMode ? (options.additionalPaymentsToSave || []) : [];
        const formData = new FormData();
        const mainUploadEntries: number[] = [];
        const additionalUploadEntries: string[] = [];
        let uploadedFiles: any[] = [];

        proofOfPayment.forEach((doc, index) => {
            if (doc.file) {
                formData.append('file', doc.file);
                mainUploadEntries.push(index);
            }
        });

        additionalPaymentsToSave.forEach((payment) => {
            if (payment.proof_of_payment?.file) {
                formData.append('file', payment.proof_of_payment.file);
                additionalUploadEntries.push(payment.id);
            }
        });

        if (mainUploadEntries.length > 0 || additionalUploadEntries.length > 0) {
            const uploadDocument: any = await addDocument(formData);
            if (getUploadResponseStatus(uploadDocument) !== 200) {
                throw new Error('Upload failed');
            }
            uploadedFiles = getUploadResponseFiles(uploadDocument);
        }

        const mainUploadedFileNames: string[] = [];
        const additionalProofFileNames: Record<string, string> = {};
        let uploadedFileIndex = 0;

        mainUploadEntries.forEach(() => {
            const uploadedFileName = uploadedFiles[uploadedFileIndex]?.file_name || '';
            if (uploadedFileName) mainUploadedFileNames.push(uploadedFileName);
            uploadedFileIndex += 1;
        });

        additionalUploadEntries.forEach((paymentId) => {
            const uploadedFileName = uploadedFiles[uploadedFileIndex]?.file_name || '';
            if (uploadedFileName) additionalProofFileNames[paymentId] = uploadedFileName;
            uploadedFileIndex += 1;
        });

        const paymentForm = { ...paymentFormState };
        delete paymentForm.basic_premium;
        delete paymentForm.ads_premium;
        delete paymentForm.admin_fee;
        delete paymentForm.net_premium;
        if (!isWithXendit) {
            paymentForm.excess_amount_with_oto = roundAmount(
                getAdditionalPaymentBaseExcessAmount(paymentFormState, additionalPaymentsToSave) +
                getAdditionalPaymentTotal(additionalPaymentsToSave)
            );
        }

        const payload: any = { ...normalizePaymentPayloadAmounts(paymentForm), lead_id: id, isWithXendit };

        if (mainUploadedFileNames.length > 0) {
            payload.payment_proof = mainUploadedFileNames;
        }

        payload.is_addtional_payment_added = additionalPaymentsToSave.length > 0;
        payload.additional_payments = buildAdditionalPaymentsPayload(additionalPaymentsToSave, additionalProofFileNames, paymentFormState);

        const response: any = await updateLeadPaymentConfig(payload);
        if (response?.status === 200) {
            reloadEmiDetails();
            return true;
        }

        return false;
    };

    const handleSubmit = async () => {
        try{
            const submitMode = paymentSubmitMode;
            setShowConfirmation(false);

            if (submitMode === 'additional') {
                if (!validate()) {
                    return;
                }
                if (!validateAdditionalPayments()) {
                    return;
                }
                const isSaved = await submitPaymentConfig(form, { additionalPaymentsToSave: additionalPayments });
                if (isSaved) {
                    setAdditionalPayments([]);
                    setAdditionalPaymentErrors({});
                }
                return;
            }

            if (!validate()) {
                return;
            }

            const paymentFormState = { ...form };
            if (isNonXenditPaymentMode && additionalPayments.length > 0) {
                const additionalPaymentTotal = getAdditionalPaymentTotal(additionalPayments);
                const totalPremiumAmountReceived = roundAmount(
                    toAmount(paymentFormState.total_premium_amount_received) - additionalPaymentTotal
                );

                paymentFormState.total_premium_amount_received = totalPremiumAmountReceived;
                paymentFormState.excess_amount_with_oto = roundAmount(
                    totalPremiumAmountReceived - toAmount(paymentFormState.insurance_premium_amount)
                );
            }

            await submitPaymentConfig(paymentFormState);
        }catch (error) {
            console.error("Error while updating EMI detail:", error);
        }finally {
            setLoading(false);
        }
    }

    const validate = () => {
        const newErrors: any = {};
        const requiredFields = [
            "payment_made_from",
            "payment_made_to",
            "gross_written_premium",
            "oto_commission_rate",
            "oto_gross_profit",
            "customer_discount_percent",
            "customer_discount_value",
            "insurance_premium_amount",
            "total_premium_amount_received",
            "payment_method",
            "premium_paid_date",
            // "payment_refrence_number",
            "transaction_fee_paid_by_customer",
            ...(isFormDisabled ? ["amount_received_from_partner_after_remittance"] : []),
            ...(isWithoutXenditCustomer
            ? ["payment_status"]
            : []),
            ...((isWithoutXenditAgent || isWithXendit)
            ? ["agent_commission_percent", "agent_commission_value","xendit_fee","other_income"]
            : []),
            ...((!isWithXendit)
            ? ["excess_amount_with_oto"]
            : [])
        ];
        requiredFields.forEach((key) => {
            if (form[key] === null || form[key] === undefined || form[key] === "") {
                newErrors[key] = "This field is required";
            }
        });
        const numericFields = [
            "gross_written_premium",
            "oto_commission_rate",
            "oto_gross_profit",
            "customer_discount_percent",
            "customer_discount_value",
            "insurance_premium_amount",
            "total_premium_amount_received",
            "transaction_fee_paid_by_customer",
            "xendit_fee",
            "other_income",
            ...((isWithoutXenditAgent || isWithXendit)
            ? ["agent_commission_percent", "agent_commission_value"]
            : []),
            ...((!isWithXendit)
            ? ["excess_amount_with_oto"]
            : [])
        ];
        numericFields.forEach((key) => {
            if (form[key] === null || form[key] === undefined || form[key] === "") {
                newErrors[key] = "Only numeric value allowed";
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateAdditionalPayments = () => {
        if (additionalPayments.length === 0) {
            setAdditionalPaymentErrors({});
            return true;
        }

        if (!isNonXenditPaymentMode) {
            setAdditionalPaymentErrors({});
            return true;
        }

        const newErrors: Record<string, string> = {};
        const additionalPaymentTotal = getAdditionalPaymentTotal(additionalPayments);
        const baseExcessAmount = getAdditionalPaymentBaseExcessAmount(form, additionalPayments);
        const baseOutstandingAmount = getOutstandingAmount(baseExcessAmount);
        let runningPaymentTotal = 0;

        additionalPayments.forEach((payment) => {
            const amount = toAmount(payment.payment_amount_received);

            if (isEmptyValue(payment.payment_made_from)) {
                newErrors[`payment_made_from_${payment.id}`] = 'Payment Made From is required';
            }

            if (isEmptyValue(payment.payment_made_to)) {
                newErrors[`payment_made_to_${payment.id}`] = 'Payment Made To is required';
            }

            if (!payment.payment_amount_received) {
                newErrors[`payment_amount_received_${payment.id}`] = 'Payment Amount Received is required';
            } else if (amount <= 0) {
                newErrors[`payment_amount_received_${payment.id}`] = 'Payment Amount Received must be greater than 0';
            }

            runningPaymentTotal = roundAmount(runningPaymentTotal + amount);
            if (runningPaymentTotal > baseOutstandingAmount) {
                newErrors[`payment_amount_received_${payment.id}`] = 'Additional payment amount must not exceed current outstanding amount';
            }

            if (!payment.payment_received_date) {
                newErrors[`payment_received_date_${payment.id}`] = 'Payment Received Date is required';
            } else if (isFutureDate(payment.payment_received_date)) {
                newErrors[`payment_received_date_${payment.id}`] = 'Payment Received Date cannot be a future date';
            }

            if (!payment.proof_of_payment?.file) {
                newErrors[`proof_of_payment_${payment.id}`] = 'Proof of Payment is required';
            } else if (!ALLOWED_PAYMENT_PROOF_TYPES.includes(payment.proof_of_payment.file.type)) {
                newErrors[`proof_of_payment_${payment.id}`] = 'Only PNG, JPG, or PDF files are allowed';
            }
        });

        if (totalAdditionalPaymentCount > MAX_ADDITIONAL_PAYMENTS) {
            newErrors.additional_payments = 'Maximum 10 additional payment transactions can be added';
        }

        if (baseExcessAmount >= 0) {
            newErrors.additional_payments = 'Additional payment can be added only when outstanding amount exists';
        } else if (roundAmount(baseExcessAmount + additionalPaymentTotal) > 0) {
            newErrors.additional_payments = 'Additional payments cannot create overpayment';
        }

        setAdditionalPaymentErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const validatePayment = async () => {
        if (!validate()) return;
        setPaymentSubmitMode('main');
        setShowConfirmation(true);
    }

    const validateAdditionalPayment = async () => {
        if (!validate()) return;
        if (!validateAdditionalPayments()) return;
        setPaymentSubmitMode('additional');
        setShowConfirmation(true);
    }

    const handleFieldChange = (field: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [field]: value }));
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const handleAddAdditionalPayment = () => {
        if (!shouldShowOutstandingRecovery) {
            return;
        }

        if (isAdditionalPaymentLimitReached) {
            setAdditionalPaymentErrors((prev) => ({
                ...prev,
                additional_payments: 'Maximum 10 additional payment transactions can be added',
            }));
            return;
        }

        setAdditionalPaymentErrors((prev) => {
            const updated = { ...prev };
            delete updated.additional_payments;
            return updated;
        });
        setAdditionalPayments((prev) => [...prev, createAdditionalPayment()]);
    };

    const handleAdditionalPaymentChange = (index: number, field: keyof AdditionalPayment, value: any) => {
        if (field === 'payment_amount_received' && value !== '' && !RAGEX_NUMBER_UPTO_2_DECIMAL_PLACES.test(value)) {
            return;
        }

        setAdditionalPayments((prevPayments) => {
            const currentPayment = prevPayments[index];
            if (!currentPayment) return prevPayments;

            if (field === 'payment_amount_received') {
                const oldAmount = toAmount(currentPayment.payment_amount_received);
                const nextAmount = toAmount(value);
                const totalBeforeChange = getAdditionalPaymentTotal(prevPayments);
                const baseExcessAmount = getAdditionalPaymentBaseExcessAmount(form, prevPayments);
                const baseOutstandingAmount = getOutstandingAmount(baseExcessAmount);
                const otherPaymentsTotal = roundAmount(totalBeforeChange - oldAmount);
                const maxAllowedForCurrentPayment = roundAmount(Math.max(baseOutstandingAmount - otherPaymentsTotal, 0));

                if (nextAmount > maxAllowedForCurrentPayment) {
                    setAdditionalPaymentErrors((prev) => ({
                        ...prev,
                        [`payment_amount_received_${currentPayment.id}`]: 'Additional payment amount must not exceed current outstanding amount',
                    }));
                    return prevPayments;
                }

                const amountDelta = roundAmount(nextAmount - oldAmount);
                setForm((prev: any) => ({
                    ...prev,
                    total_premium_amount_received: roundAmount(toAmount(prev.total_premium_amount_received) + amountDelta),
                }));
            }

            const updatedPayments = [...prevPayments];
            updatedPayments[index] = { ...currentPayment, [field]: value };

            setAdditionalPaymentErrors((prev) => {
                const updated = { ...prev };
                delete updated[`${field}_${currentPayment.id}`];
                if (field === 'payment_amount_received') {
                    delete updated[`payment_amount_received_${currentPayment.id}`];
                }
                delete updated.additional_payments;
                return updated;
            });

            return updatedPayments;
        });
    };

    const handleAdditionalPaymentProofUpload = (index: number, file: File) => {
        setAdditionalPayments((prevPayments) =>
            prevPayments.map((payment, paymentIndex) => (
                paymentIndex === index
                ? {
                    ...payment,
                    proof_of_payment: {
                        file,
                        doc_path: URL.createObjectURL(file),
                    },
                }
                : payment
            ))
        );
        setAdditionalPaymentErrors((prev) => {
            const updated = { ...prev };
            const paymentId = additionalPayments[index]?.id;
            if (paymentId) delete updated[`proof_of_payment_${paymentId}`];
            return updated;
        });
    };

    const handleRemoveAdditionalPaymentFile = (index: number) => {
        setAdditionalPayments((prevPayments) =>
            prevPayments.map((payment, paymentIndex) =>
                paymentIndex === index ? { ...payment, proof_of_payment: undefined } : payment
            )
        );
    };

    const handleRemoveAdditionalPayment = (index: number) => {
        const paymentToRemove = additionalPayments[index];
        const paymentId = paymentToRemove?.id;
        const amountToRemove = toAmount(paymentToRemove?.payment_amount_received);

        if (amountToRemove > 0) {
            setForm((prev: any) => ({
                ...prev,
                total_premium_amount_received: roundAmount(toAmount(prev.total_premium_amount_received) - amountToRemove),
            }));
        }

        setAdditionalPayments((prevPayments) => prevPayments.filter((_, paymentIndex) => paymentIndex !== index));
        setAdditionalPaymentErrors((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach((key) => {
                if (paymentId && key.includes(paymentId)) delete updated[key];
            });
            return updated;
        });
    };

    const getAdditionalPaymentRowExcessAmount = (index: number) => {
        return getAdditionalPaymentExcessAmount(form, additionalPayments, index);
    };

    const getSavedAdditionalPaymentRowExcessAmount = (index: number) => {
        return savedAdditionalPayments[index]?.excess_amount_with_oto ?? '';
    };

    const handleOffsetAgentCommissionSubmit = async (params: { offsetAmount: number }) => {
        if (isOffsetAgentCommissionUsed) {
            return;
        }

        const offsetAmount = roundAmount(toAmount(params.offsetAmount));
        const currentAgentCommission = toAmount(form.agent_commission_value);

        if (offsetAmount <= 0 || offsetAmount > currentAgentCommission) {
            return;
        }

        try {
            setLoading(true);
            const response: any = await offsetAgentCommission({
                lead_id: id,
                offset_agent_commission_amount: offsetAmount,
            });

            if (response?.status === 200) {
                setShowOffsetAgentCommissionPopup(false);
                setAdditionalPayments([]);
                setAdditionalPaymentErrors({});

                const updatedPaymentConfig = response?.data;
                setOffsetAgentCommissionInfo({
                    is_agent_commission_offset: updatedPaymentConfig?.is_agent_commission_offset ?? true,
                    offset_agent_commission_amount: updatedPaymentConfig?.offset_agent_commission_amount ?? offsetAmount,
                });
                if (updatedPaymentConfig && typeof updatedPaymentConfig === 'object') {
                    const updatedFields: any = {};
                    if (updatedPaymentConfig.agent_commission !== undefined) {
                        updatedFields.agent_commission_value = updatedPaymentConfig.agent_commission;
                    }
                    if (updatedPaymentConfig.received_amount !== undefined) {
                        updatedFields.total_premium_amount_received = updatedPaymentConfig.received_amount;
                    }
                    if (updatedPaymentConfig.excess_oto_amount !== undefined) {
                        updatedFields.excess_amount_with_oto = updatedPaymentConfig.excess_oto_amount;
                    }
                    if (Object.keys(updatedFields).length > 0) {
                        setForm((prev: any) => ({ ...prev, ...updatedFields }));
                    }
                }

                reloadEmiDetails();
            }
        } catch (error) {
            console.error('Error while offsetting agent commission:', error);
        } finally {
            setLoading(false);
        }
    };

    const getMasterConfigApi = () => {
        setLoading(true);
        dispatch(getMasterConfig())
        .then((res: any) => {
            if (res && res.status === 200) {
                if (res.data) {
                    setPaymentMadeFrom(res?.data?.payment_made_from)
                    setPaidTo(res?.data?.paid_to);
                    setPaymentStatus(res?.data?.payment_status);
                    setPaymentMethod(res?.data?.payment_method)
                }
            }
        })
        .catch((err: Error) => {
            console.error("Error fetching master config:", err);
        })
        .finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        if (id) {
            getMasterConfigApi();
        }
        // eslint-disable-next-line
    }, [id]);

    useEffect(() => {
        if (paymentConfig) {
            const grossWrittenPremium = Number(paymentConfig.gross_written_premium || 0);
            const customerDiscount = Number(paymentConfig.customer_discount || 0);
            const adminFee = Number(paymentConfig.admin_fee || 0);
            const transactionFee = Number(paymentConfig.transaction_fee || 0);
            const netPremium = Number((grossWrittenPremium - customerDiscount).toFixed(2));
            const customerInvoiceAmount = Number((netPremium + adminFee + transactionFee).toFixed(2));
            const savedPayments = mapSavedAdditionalPayments(paymentConfig?.additional_payments || []);
            const latestSavedExcessAmount = savedPayments[savedPayments.length - 1]?.excess_amount_with_oto;
            const hasSavedExcessAmount = latestSavedExcessAmount !== null && latestSavedExcessAmount !== undefined && latestSavedExcessAmount !== '';
            const hasOffsetAgentCommission = isTruthyValue(paymentConfig.is_agent_commission_offset) || toAmount(paymentConfig.offset_agent_commission_amount) > 0;
            const effectiveExcessAmount = hasSavedExcessAmount && !hasOffsetAgentCommission ? latestSavedExcessAmount : paymentConfig.excess_oto_amount;
            const effectiveReceivedAmount = hasSavedExcessAmount && !hasOffsetAgentCommission
                ? roundAmount(customerInvoiceAmount + toAmount(latestSavedExcessAmount))
                : paymentConfig.received_amount;
            setForm({
                payment_made_from: paymentConfig.payment_from,
                payment_made_to: paymentConfig.payment_to,
                payment_status: paymentConfig.payment_status,
                basic_premium: paymentConfig.basic_premium,
                ads_premium: paymentConfig.ads_premium,
                admin_fee: paymentConfig.admin_fee,
                net_premium: paymentConfig.net_premium ?? netPremium,
                gross_written_premium: paymentConfig.gross_written_premium,
                oto_commission_rate: paymentConfig.oto_commission_rate,
                oto_gross_profit: paymentConfig.oto_gross_profit,
                customer_discount_percent: paymentConfig.customer_discount_per,
                customer_discount_value: paymentConfig.customer_discount,
                agent_commission_percent: paymentConfig.agent_commission_per,
                agent_commission_value: paymentConfig.agent_commission,
                insurance_premium_amount: customerInvoiceAmount,
                total_premium_amount_received: effectiveReceivedAmount,
                excess_amount_with_oto: effectiveExcessAmount,
                payment_method: paymentConfig.payment_method,
                premium_paid_date: paymentConfig.premium_paid_date,
                payment_refrence_number: paymentConfig.payment_reference_no,
                transaction_fee_paid_by_customer: paymentConfig.transaction_fee,
                xendit_fee: paymentConfig.xendit_fee,
                other_income: paymentConfig.other_income,
                amount_received_from_partner_after_remittance:paymentConfig.amount_received_from_partner_after_remittance
            });
            const apiData: PaymentProof[] = paymentConfig?.payment_proof || [];
            const slots: PaymentProof[] = [{} as PaymentProof,{} as PaymentProof];
            apiData.forEach((doc, i) => {
                if (i < 2) slots[i] = doc;
            });
            setProofOfPayment(slots);
            setSavedAdditionalPayments(savedPayments);
            setAdditionalPayments([]);
            setAdditionalPaymentErrors({});
            setOffsetAgentCommissionInfo({
                is_agent_commission_offset: paymentConfig.is_agent_commission_offset,
                offset_agent_commission_amount: paymentConfig.offset_agent_commission_amount,
            });
        }
    }, [paymentConfig]);

    useEffect(() => {
        if (isNonXenditPaymentMode || additionalPayments.length === 0) {
            return;
        }

        const additionalPaymentTotal = getAdditionalPaymentTotal(additionalPayments);
        setForm((prev: any) => ({
            ...prev,
            total_premium_amount_received: roundAmount(toAmount(prev.total_premium_amount_received) - additionalPaymentTotal),
        }));
        setAdditionalPayments([]);
        setAdditionalPaymentErrors({});
    }, [isNonXenditPaymentMode, additionalPayments]);

    useEffect(() => {
        const invoiceFields = [
            form.gross_written_premium,
            form.customer_discount_value,
            form.admin_fee,
            form.transaction_fee_paid_by_customer
        ];

        if (invoiceFields.every((value) => value === null || value === undefined || value === "")) {
            return;
        }

        const customerInvoiceAmount = Number((
            Number(form.gross_written_premium || 0) -
            Number(form.customer_discount_value || 0) +
            Number(form.admin_fee || 0) +
            Number(form.transaction_fee_paid_by_customer || 0)
        ).toFixed(2));

        setForm((prev: any) => {
            if (Number(prev.insurance_premium_amount || 0) === customerInvoiceAmount) {
                return prev;
            }
            return { ...prev, insurance_premium_amount: customerInvoiceAmount };
        });
    }, [form.gross_written_premium, form.customer_discount_value, form.admin_fee, form.transaction_fee_paid_by_customer]);

    useEffect(() => {
        if (!isWithXendit) {
            const insurancePremium = Number(form.insurance_premium_amount || 0);
            const totalReceived = toAmount(form.total_premium_amount_received);
            const additionalPaymentTotal = getAdditionalPaymentTotal(additionalPayments);

            const baseExcessAmount = getAdditionalPaymentBaseExcessAmount(
                {
                    insurance_premium_amount: insurancePremium,
                    total_premium_amount_received: totalReceived,
                },
                additionalPayments
            );
            const excessAmount = roundAmount(baseExcessAmount + additionalPaymentTotal);

            setForm((prev: any) => {
                if (toAmount(prev.excess_amount_with_oto) === excessAmount) {
                    return prev;
                }
                return { ...prev, excess_amount_with_oto: excessAmount };
            });
        }
    }, [form.total_premium_amount_received, form.insurance_premium_amount, additionalPayments, isWithXendit]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const updated = [...proofOfPayment];
        updated[index] = {
            ...updated[index],
            file,
            doc_path: URL.createObjectURL(file),
        };

        setProofOfPayment(updated);
    };

    const removeFile = (index: number) => {
        const updated = [...proofOfPayment];
        if(updated[index]?.id){
            setRecordToBeDeleted(updated[index]);
            setDeleteConfirmation(true);
        }else{
            updated[index] = {} as PaymentProof;
            setProofOfPayment(updated);
        }
    };

    const getFileName = (doc: any) => {
        // If user uploaded file
        if (doc?.file?.name) return doc.file.name;

        // If backend URL exists
        const url = doc?.original_url || doc?.doc_path;
        if (!url) return "Upload Proof";

        return url.split("/").pop(); // extract filename from URL
    };

    const handleDeleteDoc = async () => {
        try{
            setDeleteConfirmation(false);
            setLoading(true)
            const response:any = await DeleteDocProof({id:+recordToBeDeleted.id});
            if (response?.status === 200) {
                reloadEmiDetails();
            }
        }catch (error) {
            console.error("Error while Deleting Doc:", error);
        }finally {
            setLoading(false);
        }
    }

    return (
        <>
            <FullPageLoader show={loading} />
            <div className="form-tab-right-panel">
                <div className="lead-detail-from-outer" style={{ position: 'relative' }}>
                    <div className="lead-form-heading">
                        <h2>Insurance Premium Payment</h2>
                    </div>
                    <div className="row">
                        <fieldset className="single-select col-md-3">
                            <div className="material">
                                <Select
                                    components={{ ValueContainer: CustomValueContainer }}
                                    options={paymentMadeFrom}
                                    placeholder="Payment Made From"
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    value={getSelectedOption(paymentMadeFrom, form.payment_made_from)}
                                    onChange={(option) => handleFieldChange("payment_made_from", option?.value)}
                                    isDisabled={isWithXendit || isFormDisabled}
                                />
                                {errors?.payment_made_from && (
                                    <span className="error">{errors?.payment_made_from}</span>
                                )}
                            </div>
                        </fieldset>
                        <fieldset className="single-select col-md-3">
                            <div className="material">
                                <Select
                                    components={{ ValueContainer: CustomValueContainer }}
                                    options={paidTo}
                                    placeholder="Payment Made To"
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    value={getSelectedOption(paidTo, form.payment_made_to)}
                                    onChange={(option) => handleFieldChange("payment_made_to", option?.value)}
                                    isDisabled={isWithXendit || isFormDisabled}
                                />
                            
                            {errors?.payment_made_to && (
                                <span className="error">{errors?.payment_made_to}</span>
                            )}
                            </div>
                        </fieldset>
                        {
                            isWithoutXenditCustomer &&
                            <fieldset className="single-select col-md-3">
                                <div className="material">
                                    <Select
                                        components={{ ValueContainer: CustomValueContainer }}
                                    options={paymentStatus}
                                    placeholder="Payment Status"
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    value={getSelectedOption(paymentStatus, form.payment_status)}
                                    onChange={(option) => handleFieldChange("payment_status", option?.value)}
                                    isDisabled={isFormDisabled}
                                    />
                                
                                {errors?.payment_status && (
                                    <span className="error">{errors?.payment_status}</span>
                                )}
                                </div>
                            </fieldset>
                        }
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="basic_premium" className="form-input" value={ HELPER.addCurrencyFormatting(form.basic_premium) } onChange={(e)=>handleFieldChange("basic_premium",HELPER.removeCurrencyFormattingId(e.target.value))} disabled/>
                                <label data-label="Basic Premium" className="form-label"></label>
                                {errors?.basic_premium && <div className="error text-danger mb-2"><div>{errors.basic_premium}</div></div>}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="ads_premium" className="form-input" value={ HELPER.addCurrencyFormatting(form.ads_premium) } onChange={(e)=>handleFieldChange("ads_premium",HELPER.removeCurrencyFormattingId(e.target.value))} disabled/>
                                <label data-label="Add-On Premium" className="form-label"></label>
                                {errors?.ads_premium && <div className="error text-danger mb-2"><div>{errors.ads_premium}</div></div>}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="admin_fee" className="form-input" value={ HELPER.addCurrencyFormatting(form.admin_fee) } onChange={(e)=>handleFieldChange("admin_fee",HELPER.removeCurrencyFormattingId(e.target.value))} disabled/>
                                <label data-label="Admin Fees (OTO to Customer/Agent)" className="form-label"></label>
                                {errors?.admin_fee && <div className="error text-danger mb-2"><div>{errors.admin_fee}</div></div>}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="net_premium" className="form-input" value={ HELPER.addCurrencyFormatting(form.net_premium) } onChange={(e)=>handleFieldChange("net_premium",HELPER.removeCurrencyFormattingId(e.target.value))} disabled/>
                                <label data-label="Net Written Premium" className="form-label"></label>
                                {errors?.net_premium && <div className="error text-danger mb-2"><div>{errors.net_premium}</div></div>}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="gross_written_premium" className="form-input" value={ HELPER.addCurrencyFormatting(form.gross_written_premium) } onChange={(e) => handleFieldChange("gross_written_premium", HELPER.removeCurrencyFormattingId(e.target.value))} disabled/>
                                <label data-label="Gross Written Premium" className="form-label"></label>
                                {(errors?.gross_written_premium) && (
                                    <div className="error text-danger mb-2">
                                        {errors?.gross_written_premium && <div>{errors?.gross_written_premium}</div>}
                                    </div>
                                )}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="oto_commission_rate" className="form-input" value={form.oto_commission_rate ? `${form.oto_commission_rate}%` : ""} 
                                onChange={(e) => {
                                    const rawValue = e.target.value.replace(/[^0-9.]/g, "");
                                    if (rawValue === "") {
                                        handleFieldChange("oto_commission_rate", "");
                                        return;
                                    }
                                    const numericValue = Number(rawValue);
                                    if (numericValue <= 100) {
                                        handleFieldChange("oto_commission_rate", numericValue);
                                    } else {
                                        setErrors((prev: any) => ({ ...prev, oto_commission_rate: "commission rate can't be more than 100" }));
                                    }
                                }} disabled={isFormDisabled}/>
                                <label data-label="OTO's Commission Rate" className="form-label"></label>
                                {(errors?.oto_commission_rate) && (
                                    <div className="error text-danger mb-2">
                                        {errors?.oto_commission_rate && <div>{errors?.oto_commission_rate}</div>}
                                    </div>
                                )}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="oto_gross_profit" className="form-input" value={form.oto_gross_profit ? `${form.oto_gross_profit}%` : ""} 
                                disabled
                                onChange={(e) => {
                                    const rawValue = e.target.value.replace(/[^0-9.]/g, "");
                                    handleFieldChange("oto_gross_profit", rawValue);
                                }}/>
                                <label data-label="OTO's Gross Profit %" className="form-label"></label>
                                {(errors?.oto_gross_profit) && (
                                    <div className="error text-danger mb-2">
                                        {errors?.oto_gross_profit && <div>{errors?.oto_gross_profit}</div>}
                                    </div>
                                )}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="customer_discount_percent" className="form-input" value={form.customer_discount_percent !== undefined && form.customer_discount_percent !== null && form.customer_discount_percent !== "" ? `- ${form.customer_discount_percent}%` : ""} 
                                onChange={(e) => {
                                    const rawValue = e.target.value.replace(/[^0-9.]/g, "");
                                    handleFieldChange("customer_discount_percent", rawValue);
                                }} disabled/>
                                <label data-label="Customer Discount %" className="form-label"></label>
                                {errors?.customer_discount_percent && <div className="error text-danger mb-2"><div>{errors.customer_discount_percent}</div></div>}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3">
                            <div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="customer_discount_value" className="form-input" value={form.customer_discount_value !== undefined && form.customer_discount_value !== null && form.customer_discount_value !== "" ? `- ${HELPER.addCurrencyFormatting(form.customer_discount_value)}` : ""} onChange={(e)=>handleFieldChange("customer_discount_value",HELPER.removeCurrencyFormattingId(e.target.value))} disabled/>
                                <label data-label="Customer Discount Value" className="form-label"></label>
                                {errors?.customer_discount_value && <div className="error text-danger mb-2"><div>{errors.customer_discount_value}</div></div>}
                            </div>
                        </fieldset>
                        {
                            (isWithoutXenditAgent || isWithXendit) &&
                            <>
                                <fieldset className="col-md-3">
                                    <div className="material">
                                        <input spellCheck={false} type="text" placeholder=" " name="agent_commission_percent" className="form-input" value={form.agent_commission_percent ? `${form.agent_commission_percent}%` : ""} 
                                        disabled={true}
                                        onChange={(e) => {
                                            const rawValue = e.target.value.replace(/[^0-9.]/g, "");
                                            handleFieldChange("agent_commission_percent", rawValue);
                                        }}/>
                                        <label data-label="Agent Commision (%)" className="form-label"></label>
                                        {errors?.agent_commission_percent && <div className="error text-danger mb-2"><div>{errors.agent_commission_percent}</div></div>}
                                    </div>
                                </fieldset>
                                <fieldset className="col-md-3"><div className="material">
                                    <input spellCheck={false} type="text" placeholder=" " name="agent_commission_value" className="form-input" value={ HELPER.addCurrencyFormatting(form.agent_commission_value) } onChange={(e)=>handleFieldChange("agent_commission_value",HELPER.removeCurrencyFormattingId(e.target.value))} disabled={true}/>
                                    <label data-label="Agent Commision Value" className="form-label"></label>
                                        {errors?.agent_commission_value && <div className="error text-danger mb-2"><div>{errors.agent_commission_value}</div></div>}
                                    </div>
                                </fieldset>
                            </>
                        }
                        <fieldset className="col-md-3"><div className="material">
                            <input spellCheck={false} type="text" placeholder=" " name="insurance_premium_amount" className="form-input" value={ HELPER.addCurrencyFormatting(form.insurance_premium_amount) } onChange={(e)=>handleFieldChange("insurance_premium_amount",HELPER.removeCurrencyFormattingId(e.target.value))} disabled/>
                            <label data-label="Customer Invoice Amount" className="form-label"></label>
                                {errors?.insurance_premium_amount && <div className="error text-danger mb-2"><div>{errors.insurance_premium_amount}</div></div>}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3"><div className="material total-premium-received-field">
                            <input spellCheck={false} type="text" placeholder=" " name="total_premium_amount_received" className="form-input" value={ HELPER.addIdDecimalCurrencyFormatting(form.total_premium_amount_received) } onChange={(e)=>handleFieldChange("total_premium_amount_received",HELPER.normalizeIdDecimalAmount(e.target.value, true))} disabled={isWithXendit || isFormDisabled}/>
                            <label data-label="Total Premium Amount Received" className="form-label"></label>
                            {shouldShowOffsetAgentCommissionInfo && (
                                <>
                                    <span
                                        className="offset-agent-commission-info"
                                        data-tooltip-id="offset-agent-commission-info-tooltip"
                                        data-tooltip-content={offsetAgentCommissionTooltip}
                                        aria-label={offsetAgentCommissionTooltip}
                                        tabIndex={0}
                                    >
                                        <i className="ic-info-icon1"></i>
                                    </span>
                                    <TooltipLite
                                        id="offset-agent-commission-info-tooltip"
                                        place="top"
                                        style={{ backgroundColor: '#1E3A5F' }}
                                        arrowColor="#1E3A5F"
                                    />
                                </>
                            )}
                                {errors?.total_premium_amount_received && <div className="error text-danger mb-2"><div>{errors.total_premium_amount_received}</div></div>}
                            </div>
                        </fieldset>
                        {
                            !isWithXendit &&
                            <fieldset className="col-md-3"><div className="material">
                                <input spellCheck={false} type="text" placeholder=" " name="excess_amount_with_oto" className="form-input" value={ HELPER.addCurrencyFormatting(form.excess_amount_with_oto) } onChange={(e)=>handleFieldChange("excess_amount_with_oto",HELPER.removeCurrencyFormattingId(e.target.value))} disabled={!isWithXendit || isFormDisabled}/>
                                <label data-label="Excess Amount with OTO" className="form-label"></label>
                                    {errors?.excess_amount_with_oto && <div className="error text-danger mb-2"><div>{errors.excess_amount_with_oto}</div></div>}
                                </div>
                            </fieldset>
                        }
                        <fieldset className="single-select col-md-3">
                            <div className="material">
                                <Select
                                    components={{ ValueContainer: CustomValueContainer }}
                                    options={paymentMethod}
                                    placeholder="Payment Method"
                                    className="react-select"
                                    classNamePrefix="react-select"
                                    value={getSelectedOption(paymentMethod, form.payment_method)}
                                    onChange={(option) => handleFieldChange("payment_method", option?.value)}
                                    isDisabled={isWithXendit || isFormDisabled}
                                />
                                {errors?.payment_method && (<span className="error">{errors?.payment_method}</span>)}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3 receiveDateShow">
                            <div className="material">
                                <ReceivedDate
                                    placeholder="Premium Paid Date"
                                    selectedDate={form.premium_paid_date ? new Date(form.premium_paid_date) : null}
                                    onDateChange={(date:any) => handleFieldChange("premium_paid_date", date?.toISOString())}
                                    disabled={isWithXendit || isFormDisabled}
                                />
                                {errors?.premium_paid_date && (
                                    <span className="error">{errors?.premium_paid_date}</span>
                                )}
                            </div>
                        </fieldset>   
                        <fieldset className="col-md-3"><div className="material">
                            <input type="text" placeholder=" " name="payment_refrence_number" className="form-input" value={ form.payment_refrence_number || ""} onChange={(e)=>handleFieldChange("payment_refrence_number",e.target.value)} disabled={isFormDisabled}/>
                            <label data-label="Payment Reference No. (Optional)" className="form-label"></label>
                                {errors?.payment_refrence_number && <div className="error text-danger mb-2"><div>{errors.payment_refrence_number}</div></div>}
                            </div>
                        </fieldset>
                        <fieldset className="col-md-3"><div className="material">
                            <input spellCheck={false} type="text" placeholder=" " name="transaction_fee_paid_by_customer" className="form-input" value={ HELPER.addCurrencyFormatting(form.transaction_fee_paid_by_customer) } onChange={(e)=>handleFieldChange("transaction_fee_paid_by_customer",HELPER.removeCurrencyFormattingId(e.target.value))} disabled={isWithXendit}/>
                            <label data-label="Transaction Fee Paid By Customer*" className="form-label"></label>
                                {errors?.transaction_fee_paid_by_customer && <div className="error text-danger mb-2"><div>{errors.transaction_fee_paid_by_customer}</div></div>}
                            </div>
                        </fieldset>
                        {
                            isWithoutXenditCustomer &&
                            <fieldset className="col-md-3"></fieldset>
                        }
                        {shouldShowOutstandingRecovery && (
                            <fieldset className="col-md-12 cob-recovery-action">
                                <button
                                    type="button"
                                    className="btn-primary cob-recovery-action-btn"
                                    onClick={isOffsetAgentCommissionUsed ? undefined : () => setShowOffsetAgentCommissionIntroPopup(true)}
                                    disabled={isOffsetAgentCommissionUsed}
                                >
                                    Offset Agent Commission
                                </button>
                            </fieldset>
                        )}
                        {
                            isWithXendit && 
                            <>
                                <fieldset className="col-md-3"><div className="material">
                                    <input spellCheck={false} type="text" placeholder=" " name="xendit_fee" className="form-input" value={ HELPER.addCurrencyFormatting(form.xendit_fee) } onChange={(e)=>handleFieldChange("xendit_fee",HELPER.removeCurrencyFormattingId(e.target.value))} disabled={isWithXendit || isFormDisabled}/>
                                    <label data-label="Xendit's Fee" className="form-label"></label>
                                        {errors?.xendit_fee && <div className="error text-danger mb-2"><div>{errors.xendit_fee}</div></div>}
                                    </div>
                                </fieldset>
                                <fieldset className="col-md-3"><div className="material">
                                    <input spellCheck={false} type="text" placeholder=" " name="other_income" className="form-input" value={ HELPER.addCurrencyFormatting(form.other_income) } onChange={(e)=>handleFieldChange("other_income",HELPER.removeCurrencyFormattingId(e.target.value))} disabled={isWithXendit || isFormDisabled}/>
                                    <label data-label="Other Income" className="form-label"></label>
                                        {errors?.other_income && <div className="error text-danger mb-2"><div>{errors.other_income}</div></div>}
                                    </div>
                                </fieldset>
                            </>
                        }
                        {!isWithXendit && (
                            <>
                                {proofOfPayment.map((doc, index) => (
                                    <fieldset className="col-md-3" key={doc.id || index}>
                                        <div className="attech-repair-doc">
                                            <div className="upload-text">
                                                <i className="ic-attach_file"></i>
                                                <input type="file" name="repairDocs" onChange={(e) => handleFileUpload(e, index)} disabled={isFormDisabled}/>
                                                <p>Payment Proof</p>
                                            </div>
                                            {doc?.doc_path &&
                                            <div className="uploaded-file">
                                                <div className="upload-file-name">
                                                    <a
                                                        href={doc.doc_path}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {getFileName(doc)}
                                                    </a>
                                                </div>
                                                {
                                                    !isPushedToRemittance &&
                                                    <span className="remove-icon" onClick={() => removeFile(index)}>
                                                        ✕
                                                    </span>
                                                }
                                            </div>
                                            }
                                        </div>
                                    </fieldset>
                                ))}
                            </>
                        )}
                    </div>
                    <AdditionalPaymentSection
                        payments={savedAdditionalPayments}
                        paymentMadeFromOptions={paymentMadeFrom}
                        paidToOptions={paidTo}
                        errors={{}}
                        isDisabled
                        getExcessAmountAfterPayment={getSavedAdditionalPaymentRowExcessAmount}
                        getFileName={getFileName}
                    />
                    <AdditionalPaymentSection
                        title={savedAdditionalPayments.length ? 'New Additional Payment' : 'Additional Payment'}
                        startIndex={savedAdditionalPayments.length}
                        payments={additionalPayments}
                        paymentMadeFromOptions={paymentMadeFrom}
                        paidToOptions={paidTo}
                        errors={additionalPaymentErrors}
                        onChange={handleAdditionalPaymentChange}
                        onFileUpload={handleAdditionalPaymentProofUpload}
                        onRemoveFile={handleRemoveAdditionalPaymentFile}
                        onRemovePayment={handleRemoveAdditionalPayment}
                        onSave={validateAdditionalPayment}
                        getExcessAmountAfterPayment={getAdditionalPaymentRowExcessAmount}
                        getFileName={getFileName}
                    />
                    {shouldShowOutstandingRecovery && (
                        <div className="additional-payment-actions">
                            <button
                                type="button"
                                className="btn-primary additional-payment-add-btn"
                                onClick={handleAddAdditionalPayment}
                                disabled={isAdditionalPaymentLimitReached}
                            >
                                <i className="ic-add"></i>
                                Add Another Payment
                            </button>
                            {additionalPaymentErrors.additional_payments && (
                                <span className="error">{additionalPaymentErrors.additional_payments}</span>
                            )}
                        </div>
                    )}
                    {isOutstandingPremiumFullyRecovered && (
                        <div className="additional-payment-complete-alert">
                            <i className="ic-check"></i>
                            <span>Outstanding premium fully recovered — Excess Amount with OTO is now Rp 0. Collection complete.</span>
                        </div>
                    )}
                    <div className="row p-md-t">
                        <fieldset className="col-md-4"><div className="material">
                            <input spellCheck={false} type="text" placeholder=" " name="amount_received_from_partner_after_remittance" className="form-input" value={ HELPER.addCurrencyFormatting(form.amount_received_from_partner_after_remittance) } onChange={(e)=>handleFieldChange("amount_received_from_partner_after_remittance",HELPER.removeCurrencyFormattingId(e.target.value))}/>
                            <label data-label={`Amount Received from Partner after Remittance${isFormDisabled ? "" : " (Optional)"}`} className="form-label"></label>
                                {errors?.amount_received_from_partner_after_remittance && <div className="error text-danger mb-2"><div>{errors.amount_received_from_partner_after_remittance}</div></div>}
                            </div>
                        </fieldset>
                    </div>
                    {
                        +leadSubStatusId === LEAD_LOST_SUB_STATUS_ID ?
                            <div className="lead-form-filed p-lg-t">
                                <div className="btn-continue">
                                    <button type="button" className="btn-primary" onClick={() => validatePayment()} disabled={true}>Save</button>
                                    <button type="button" className="btn-line" onClick={() => setFollowUpPopup(true)} disabled={true}>Mark Status & Follow-up</button>
                                </div>
                            </div>
                        :
                            <div className="lead-form-filed p-lg-t">
                                <div className="btn-continue">
                                    <button type="button" className="btn-primary" onClick={() => validatePayment()}>Save</button>
                                    <button type="button" className="btn-line" onClick={() => setFollowUpPopup(true)}>Mark Status & Follow-up</button>
                                </div>
                            </div>
                    }
                </div>
            </div>
            {showConfirmation === false ? ("") : (
                <ConfirmPopUp
                    confirmNo={() => setShowConfirmation(false)}
                    confirmYes={handleSubmit}
                    message={`Are you sure you want to submit?`}
                    isConfirmNotVisible={true} 
                    from={"EmiComponent"}
                />
            )}
            {deleteConfirmation === false ? ("") : (
                <ConfirmPopUp
                    confirmNo={() => {
                        setDeleteConfirmation(false);
                        setRecordToBeDeleted({});
                    }}
                    confirmYes={handleDeleteDoc}
                    message={`Are you sure you want to delete this document? You will not be able to recover it.`}
                    isConfirmNotVisible={true} 
                    from={"EmiComponent"}
                />
            )}
            {showOffsetAgentCommissionPopup && !isOffsetAgentCommissionUsed && (
                <OffsetAgentCommissionPopup
                    show={showOffsetAgentCommissionPopup}
                    excessAmount={form.excess_amount_with_oto}
                    agentCommission={form.agent_commission_value}
                    loading={loading}
                    onClose={() => setShowOffsetAgentCommissionPopup(false)}
                    onSubmit={handleOffsetAgentCommissionSubmit}
                />
            )}
            {showOffsetAgentCommissionIntroPopup && !isOffsetAgentCommissionUsed && (
                <OffsetAgentCommissionIntroPopup
                    show={showOffsetAgentCommissionIntroPopup}
                    excessAmount={form.excess_amount_with_oto}
                    agentCommission={form.agent_commission_value}
                    onClose={() => setShowOffsetAgentCommissionIntroPopup(false)}
                    onContinue={() => {
                        setShowOffsetAgentCommissionIntroPopup(false);
                        setShowOffsetAgentCommissionPopup(true);
                    }}
                />
            )}
            {followUpPopup ?
                <FollowUpPopup
                    followUpPopup={followUpPopup}
                    showFollowupPopup={(e: boolean) => setFollowUpPopup(e)}
                    leadId={paymentConfig?.lead_id}
                    showLostStatus={isLostStatusVisible}
                    reloadApi={reloadEmiDetails}
                /> : ''
            }
        </>
    )
}

export default LeadDetailFormID;
